import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Banknote, ArrowRightLeft, ReceiptText, CheckSquare, Printer, CreditCard, PlusCircle, Trash2, Ban, FileDown, AlertTriangle } from 'lucide-react';
import { money, toCents } from './money';
import ReportDocument from './ui/ReportRenderer';

const ACTIONS = [
  { key: 'register', label: 'Register' },
  { key: 'check', label: 'Write Check' },
  { key: 'print', label: 'Print Queue' },
  { key: 'cc', label: 'Credit Card' },
  { key: 'deposit', label: 'Deposits' },
  { key: 'transfer', label: 'Transfer' },
  { key: 'reconcile', label: 'Reconcile' },
];
const BS_TYPES = ['Bank', 'Credit Card', 'Other Current Asset', 'Fixed Asset', 'Other Asset',
  'Accounts Payable', 'Other Current Liability', 'Long Term Liability', 'Equity', 'Accounts Receivable'];

const downloadB64 = (b64, name) => {
  const bin = atob(b64); const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([arr], { type: 'application/pdf' }));
  const a = document.createElement('a'); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
};
const downloadBlob = (data, name, type) => {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement('a'); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
};

export default function Banking({ base, isDark = true, initialView = 'register', allowed = null }) {
  const [accounts, setAccounts] = useState([]);
  const [names, setNames] = useState([]);
  const [acctId, setAcctId] = useState('');
  const [view, setView] = useState(initialView);
  useEffect(() => { if (initialView) setView(initialView); }, [initialView]);
  const [error, setError] = useState('');
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';

  const loadAccts = useCallback(async () => {
    const res = await axios.get(`${base}/accounts`);
    const list = (res.data?.accounts || []).filter((a) => BS_TYPES.includes(a.type));
    setAccounts(list);
    setAcctId((p) => p || list.find((a) => a.type === 'Bank')?.account_id || list[0]?.account_id || '');
  }, [base]);
  const loadNames = useCallback(async () => {
    try { setNames((await axios.get(`${base}/lists/names`)).data || []); } catch { /* noop */ }
  }, [base]);
  useEffect(() => { loadAccts(); loadNames(); }, [loadAccts, loadNames]);

  const bankAccts = accounts.filter((a) => ['Bank', 'Credit Card'].includes(a.type));
  const cardAccts = accounts.filter((a) => a.type === 'Credit Card');
  const done = () => { setView('register'); };

  return (
    <div className="space-y-3" data-testid="olfin-banking">
      <div className="flex items-center gap-2 flex-wrap">
        <Banknote className="h-5 w-5 text-emerald-500" />
        <Select value={acctId} onValueChange={setAcctId}>
          <SelectTrigger className="w-72" data-testid="banking-account-picker"><SelectValue placeholder="Select account" /></SelectTrigger>
          <SelectContent>{accounts.map((a) => <SelectItem key={a.account_id} value={a.account_id}>{a.full_name} ({a.type})</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex gap-1 ml-auto flex-wrap">
          {(allowed ? ACTIONS.filter((t) => allowed.includes(t.key)) : ACTIONS).map((t) => (
            <button key={t.key} onClick={() => { setView(t.key); setError(''); }} data-testid={`banking-view-${t.key}`}
              className={`px-3 py-1.5 text-sm rounded-md ${view === t.key ? 'bg-emerald-600 text-white' : (isDark ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100')}`}>{t.label}</button>
          ))}
        </div>
      </div>
      {error && <div className="rounded border border-red-500/50 bg-red-500/10 p-2 text-sm text-red-400" data-testid="banking-error">{error}</div>}

      {view === 'register' && <Register base={base} acctId={acctId} accounts={accounts} names={names} isDark={isDark} setError={setError} />}
      {view === 'check' && <WriteCheck base={base} bankAccts={bankAccts} accounts={accounts} names={names} isDark={isDark} onDone={done} />}
      {view === 'print' && <PrintQueue base={base} bankAccts={bankAccts} isDark={isDark} />}
      {view === 'cc' && <CreditCardCharge base={base} cardAccts={cardAccts} accounts={accounts} names={names} isDark={isDark} onDone={done} />}
      {view === 'deposit' && <Deposits base={base} accounts={accounts} isDark={isDark} setError={setError} />}
      {view === 'transfer' && <Transfer base={base} accounts={accounts} isDark={isDark} onDone={done} />}
      {view === 'reconcile' && <Reconcile base={base} acctId={acctId} accounts={accounts} isDark={isDark} />}
    </div>
  );
}

const Field = ({ label, children, sub }) => (
  <label className="space-y-1 block"><span className={`text-xs ${sub}`}>{label}</span>{children}</label>
);
const panelCls = (d) => (d ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200');

function Register({ base, acctId, accounts, names, isDark, setError }) {
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';
  const [reg, setReg] = useState(null);
  const [sort, setSort] = useState('date');
  const [adding, setAdding] = useState(false);
  const load = useCallback(async () => {
    if (!acctId) return;
    try { setReg((await axios.get(`${base}/bank-register/${acctId}?sort=${sort}`)).data); }
    catch (e) { setError(e?.response?.data?.detail || 'Register failed'); }
  }, [base, acctId, sort, setError]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (glId) => {
    try { await axios.patch(`${base}/registers/line/${glId}/cleared`); await load(); }
    catch (e) {
      const d = e?.response?.data;
      if (d?.requires_confirm || (d && d.detail === undefined && d.gl_id)) { await load(); return; }
      setError(e?.response?.data?.detail || 'Toggle failed');
    }
  };
  const toggleReconciled = async (glId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('This line is reconciled — un-clearing affects a completed reconciliation. Continue?')) return;
    try { await axios.patch(`${base}/registers/line/${glId}/cleared?confirm=true`); await load(); }
    catch (e) { setError(e?.response?.data?.detail || 'Toggle failed'); }
  };
  const clickClr = async (r) => {
    if (r.cleared === 'R') { await toggleReconciled(r.gl_id); return; }
    // first check if it needs confirm
    try {
      const res = await axios.patch(`${base}/registers/line/${r.gl_id}/cleared`);
      if (res.data?.requires_confirm) { await toggleReconciled(r.gl_id); } else { await load(); }
    } catch (e) { setError(e?.response?.data?.detail || 'Toggle failed'); }
  };
  const voidCheck = async (txnId) => {
    // eslint-disable-next-line no-alert
    const mode = window.confirm('OK = void on the original date. Cancel = void in the current period (closed periods).') ? 'original' : 'current_period';
    try { await axios.post(`${base}/checks/${txnId}/void`, { mode, current_date: new Date().toISOString().slice(0, 10) }); await load(); }
    catch (e) { setError(e?.response?.data?.detail || 'Void failed'); }
  };
  const delCheck = async (txnId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this check permanently?')) return;
    try { await axios.delete(`${base}/checks/${txnId}`); await load(); }
    catch (e) { setError(e?.response?.data?.detail || 'Delete failed'); }
  };
  if (!reg) return <div className={`text-sm ${sub}`}>Select an account.</div>;
  return (
    <Card className={panelCls(isDark)}><CardContent className="p-3 space-y-2">
      <div className="flex items-center gap-2">
        <span className={`text-xs ${sub}`}>Sort</span>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-40 h-8" data-testid="register-sort"><SelectValue /></SelectTrigger>
          <SelectContent>{['date', 'number', 'amount', 'cleared'].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
        <Button size="sm" variant="outline" className="ml-auto" onClick={() => setAdding((v) => !v)} data-testid="register-add-toggle"><PlusCircle className="h-4 w-4 mr-1" />Add entry</Button>
      </div>
      {adding && <AddEntry base={base} acctId={acctId} accounts={accounts} names={names} isDark={isDark} onDone={() => { setAdding(false); load(); }} />}
      <table className="w-full text-sm"><thead>
        <tr className={`text-left ${sub} border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
          {reg.columns.map((c) => <th key={c} className="p-2">{c}</th>)}<th className="p-2" />
        </tr></thead>
        <tbody data-testid="banking-register-rows">
          {reg.rows.map((r) => (
            <tr key={r.gl_id} className={`border-b ${isDark ? 'border-slate-800' : 'border-slate-100'}`}>
              <td className="p-2">{r.date}</td>
              <td className="p-2">{r.number}</td>
              <td className="p-2">{r.memo}</td>
              <td className="p-2 text-right tabular-nums">{r.decrease ? money(r.decrease) : ''}</td>
              <td className="p-2 text-center cursor-pointer select-none" onClick={() => clickClr(r)} data-testid={`banking-clr-${r.gl_id}`}>{r.cleared || '—'}</td>
              <td className="p-2 text-right tabular-nums">{r.increase ? money(r.increase) : ''}</td>
              <td className="p-2 text-right tabular-nums">{money(r.balance)}</td>
              <td className="p-2 text-right whitespace-nowrap">
                {r.type === 'CHK' && (
                  <>
                    <button className="text-amber-500 mr-2" title="Void" onClick={() => voidCheck(r.txn_id)} data-testid={`chk-void-${r.txn_id}`}><Ban className="h-4 w-4 inline" /></button>
                    <button className="text-red-500" title="Delete" onClick={() => delCheck(r.txn_id)} data-testid={`chk-del-${r.txn_id}`}><Trash2 className="h-4 w-4 inline" /></button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody></table>
    </CardContent></Card>
  );
}

function AddEntry({ base, acctId, accounts, names, isDark, onDone }) {
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';
  const [kind, setKind] = useState('CHK');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState('');
  const [offset, setOffset] = useState('');
  const [to, setTo] = useState('');
  const [memo, setMemo] = useState('');
  const [msg, setMsg] = useState('');
  const save = async () => {
    try {
      await axios.post(`${base}/registers/${acctId}/add-entry`, {
        kind, date, amount: toCents(amount), offset_account_id: offset || null, to_id: to || null, memo });
      onDone();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="grid gap-2 sm:grid-cols-5 p-2 rounded bg-slate-900/30" data-testid="register-add-form">
      <Field label="Type" sub={sub}><Select value={kind} onValueChange={setKind}><SelectTrigger data-testid="add-kind"><SelectValue /></SelectTrigger><SelectContent>{['CHK', 'DEP', 'TRANSFER'].map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent></Select></Field>
      <Field label="Date" sub={sub}><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="add-date" /></Field>
      <Field label="Amount" sub={sub}><Input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" data-testid="add-amount" /></Field>
      {kind === 'TRANSFER'
        ? <Field label="To account" sub={sub}><Select value={to} onValueChange={setTo}><SelectTrigger data-testid="add-to"><SelectValue placeholder="To" /></SelectTrigger><SelectContent>{accounts.map((a) => <SelectItem key={a.account_id} value={a.account_id}>{a.full_name}</SelectItem>)}</SelectContent></Select></Field>
        : <Field label="Account" sub={sub}><Select value={offset} onValueChange={setOffset}><SelectTrigger data-testid="add-offset"><SelectValue placeholder="Account" /></SelectTrigger><SelectContent>{accounts.map((a) => <SelectItem key={a.account_id} value={a.account_id}>{a.full_name}</SelectItem>)}</SelectContent></Select></Field>}
      <div className="flex items-end gap-2"><Button size="sm" className="bg-emerald-600 text-white" onClick={save} disabled={!amount} data-testid="add-save">Add</Button>{msg && <span className="text-xs text-red-400">{msg}</span>}</div>
    </div>
  );
}

function WriteCheck({ base, bankAccts, accounts, names, isDark, onDone }) {
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';
  const [bank, setBank] = useState(bankAccts[0]?.account_id || '');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [payee, setPayee] = useState('');
  const [address, setAddress] = useState('');
  const [toPrint, setToPrint] = useState(true);
  const [checkNo, setCheckNo] = useState('');
  const [lines, setLines] = useState([{ account_id: '', amount: '', memo: '' }]);
  const [msg, setMsg] = useState('');
  const setLine = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const save = async () => {
    try {
      const r = await axios.post(`${base}/checks`, {
        bank_account_id: bank, date, payee_id: payee || null, address,
        to_be_printed: toPrint, check_no: toPrint ? null : (checkNo || null),
        expense_lines: lines.filter((l) => l.account_id && l.amount).map((l) => ({ account_id: l.account_id, amount: toCents(l.amount), memo: l.memo })) });
      setMsg(`Saved ${money(r.data.total)}${r.data.duplicate_check_no ? ' — DUPLICATE #!' : ''}`);
      onDone();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Save failed'); }
  };
  const payees = names.filter((n) => ['vendor', 'other', 'employee'].includes(n.name_type));
  return (
    <Card className={panelCls(isDark)} data-testid="banking-check-form"><CardContent className="p-4 grid gap-3 sm:grid-cols-2">
      <Field label="Bank account" sub={sub}><Select value={bank} onValueChange={setBank}><SelectTrigger data-testid="check-bank"><SelectValue /></SelectTrigger><SelectContent>{bankAccts.map((a) => <SelectItem key={a.account_id} value={a.account_id}>{a.full_name}</SelectItem>)}</SelectContent></Select></Field>
      <Field label="Date" sub={sub}><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="check-date" /></Field>
      <Field label="Pay to" sub={sub}><Select value={payee} onValueChange={setPayee}><SelectTrigger data-testid="check-payee"><SelectValue placeholder="Payee" /></SelectTrigger><SelectContent>{payees.map((n) => <SelectItem key={n.name_id} value={n.name_id}>{n.name}</SelectItem>)}</SelectContent></Select></Field>
      <Field label="Address" sub={sub}><Input value={address} onChange={(e) => setAddress(e.target.value)} data-testid="check-address" /></Field>
      <div className="sm:col-span-2 space-y-2">
        <span className={`text-xs ${sub}`}>Expense lines</span>
        {lines.map((l, i) => (
          <div key={i} className="grid gap-2 sm:grid-cols-3">
            <Select value={l.account_id} onValueChange={(v) => setLine(i, 'account_id', v)}><SelectTrigger data-testid={`check-line-acct-${i}`}><SelectValue placeholder="Account" /></SelectTrigger><SelectContent>{accounts.map((a) => <SelectItem key={a.account_id} value={a.account_id}>{a.full_name}</SelectItem>)}</SelectContent></Select>
            <Input value={l.amount} onChange={(e) => setLine(i, 'amount', e.target.value)} placeholder="0.00" data-testid={`check-line-amt-${i}`} />
            <Input value={l.memo} onChange={(e) => setLine(i, 'memo', e.target.value)} placeholder="Memo" data-testid={`check-line-memo-${i}`} />
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={() => setLines([...lines, { account_id: '', amount: '', memo: '' }])} data-testid="check-add-line"><PlusCircle className="h-4 w-4 mr-1" />Add line</Button>
      </div>
      <div className="sm:col-span-2 flex items-center gap-3 flex-wrap">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={toPrint} onChange={(e) => setToPrint(e.target.checked)} data-testid="check-toprint" /> To be printed</label>
        {!toPrint && <Input className="w-32" value={checkNo} onChange={(e) => setCheckNo(e.target.value)} placeholder="Check #" data-testid="check-no" />}
        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={save} disabled={!bank || !lines.some((l) => l.account_id && l.amount)} data-testid="check-save"><ReceiptText className="h-4 w-4 mr-1" />Save Check</Button>
        {msg && <span className={`text-sm ${sub}`} data-testid="check-msg">{msg}</span>}
      </div>
    </CardContent></Card>
  );
}

function PrintQueue({ base, bankAccts, isDark }) {
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';
  const [bank, setBank] = useState(bankAccts[0]?.account_id || '');
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState({});
  const [firstNo, setFirstNo] = useState('1001');
  const [msg, setMsg] = useState('');
  const load = useCallback(async () => {
    if (!bank) return;
    setRows((await axios.get(`${base}/checks/print-queue?bank=${bank}`)).data || []); setSel({});
  }, [base, bank]);
  useEffect(() => { load(); }, [load]);
  const ids = Object.keys(sel).filter((k) => sel[k]);
  const doPrint = async (reprint) => {
    try {
      const url = reprint ? `${base}/checks/print/confirm` : `${base}/checks/print`;
      const r = await axios.post(url, { bank_account_id: bank, first_no: parseInt(firstNo, 10), txn_ids: ids });
      downloadB64(r.data.pdf_base64, 'checks.pdf');
      setMsg(`Assigned ${r.data.assigned.map((a) => a.check_no).join(', ')}`); await load();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Print failed'); }
  };
  return (
    <Card className={panelCls(isDark)} data-testid="banking-print-queue"><CardContent className="p-4 space-y-3">
      <div className="flex items-end gap-2 flex-wrap">
        <Field label="Bank account" sub={sub}><Select value={bank} onValueChange={setBank}><SelectTrigger className="w-56" data-testid="pq-bank"><SelectValue /></SelectTrigger><SelectContent>{bankAccts.map((a) => <SelectItem key={a.account_id} value={a.account_id}>{a.full_name}</SelectItem>)}</SelectContent></Select></Field>
        <Field label="First check #" sub={sub}><Input className="w-28" value={firstNo} onChange={(e) => setFirstNo(e.target.value)} data-testid="pq-first-no" /></Field>
        <Button className="bg-emerald-600 text-white" onClick={() => doPrint(false)} disabled={!ids.length} data-testid="pq-print"><Printer className="h-4 w-4 mr-1" />Print &amp; assign</Button>
        <Button variant="outline" onClick={() => doPrint(true)} disabled={!ids.length} data-testid="pq-reprint">Confirm / reprint</Button>
        {msg && <span className={`text-sm ${sub}`} data-testid="pq-msg">{msg}</span>}
      </div>
      {rows.length === 0 ? <p className={`text-sm ${sub}`} data-testid="pq-empty">No checks waiting to print.</p> : (
        <table className="w-full text-sm"><tbody data-testid="pq-rows">
          {rows.map((r) => (
            <tr key={r.txn_id} className={`border-b ${isDark ? 'border-slate-800' : 'border-slate-100'}`}>
              <td className="p-2"><input type="checkbox" checked={!!sel[r.txn_id]} onChange={(e) => setSel({ ...sel, [r.txn_id]: e.target.checked })} data-testid={`pq-sel-${r.txn_id}`} /></td>
              <td className="p-2">{r.date}</td><td className="p-2">{r.payee}</td><td className="p-2">{r.memo}</td>
              <td className="p-2 text-right tabular-nums">{money(r.amount)}</td>
            </tr>
          ))}
        </tbody></table>
      )}
    </CardContent></Card>
  );
}

function CreditCardCharge({ base, cardAccts, accounts, names, isDark, onDone }) {
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';
  const [card, setCard] = useState(cardAccts[0]?.account_id || '');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [payee, setPayee] = useState('');
  const [amount, setAmount] = useState('');
  const [acct, setAcct] = useState('');
  const [refund, setRefund] = useState(false);
  const [msg, setMsg] = useState('');
  const save = async () => {
    try {
      await axios.post(`${base}/cc/charge`, { card_account_id: card, date, payee_id: payee || null, refund,
        lines: [{ account_id: acct, amount: toCents(amount) }] });
      setMsg(refund ? 'Refund posted' : 'Charge posted'); onDone();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Failed'); }
  };
  const payees = names.filter((n) => ['vendor', 'other', 'employee'].includes(n.name_type));
  return (
    <Card className={panelCls(isDark)} data-testid="banking-cc-form"><CardContent className="p-4 grid gap-3 sm:grid-cols-2">
      <Field label="Credit card" sub={sub}><Select value={card} onValueChange={setCard}><SelectTrigger data-testid="cc-card"><SelectValue placeholder="Card" /></SelectTrigger><SelectContent>{cardAccts.map((a) => <SelectItem key={a.account_id} value={a.account_id}>{a.full_name}</SelectItem>)}</SelectContent></Select></Field>
      <Field label="Date" sub={sub}><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="cc-date" /></Field>
      <Field label="Payee" sub={sub}><Select value={payee} onValueChange={setPayee}><SelectTrigger data-testid="cc-payee"><SelectValue placeholder="Payee" /></SelectTrigger><SelectContent>{payees.map((n) => <SelectItem key={n.name_id} value={n.name_id}>{n.name}</SelectItem>)}</SelectContent></Select></Field>
      <Field label="Account" sub={sub}><Select value={acct} onValueChange={setAcct}><SelectTrigger data-testid="cc-acct"><SelectValue placeholder="Account" /></SelectTrigger><SelectContent>{accounts.map((a) => <SelectItem key={a.account_id} value={a.account_id}>{a.full_name}</SelectItem>)}</SelectContent></Select></Field>
      <Field label="Amount" sub={sub}><Input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" data-testid="cc-amount" /></Field>
      <div className="flex items-end gap-3">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={refund} onChange={(e) => setRefund(e.target.checked)} data-testid="cc-refund" /> Refund/credit</label>
        <Button className="bg-emerald-600 text-white" onClick={save} disabled={!card || !acct || !amount} data-testid="cc-save"><CreditCard className="h-4 w-4 mr-1" />Post</Button>
        {msg && <span className={`text-sm ${sub}`}>{msg}</span>}
      </div>
    </CardContent></Card>
  );
}

function Deposits({ base, accounts, isDark, setError }) {
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';
  const [depTo, setDepTo] = useState(accounts.find((a) => a.type === 'Bank')?.account_id || '');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [payMeth, setPayMeth] = useState('all');
  const [sort, setSort] = useState('date');
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState({});
  const [msg, setMsg] = useState('');
  const [lastDep, setLastDep] = useState(null);
  const load = useCallback(async () => {
    const p = new URLSearchParams({ sort }); if (payMeth !== 'all') p.set('pay_meth', payMeth);
    try { setRows((await axios.get(`${base}/deposits/payments?${p}`)).data || []); }
    catch (e) { setError(e?.response?.data?.detail || 'Load failed'); }
  }, [base, payMeth, sort, setError]);
  useEffect(() => { load(); }, [load]);
  const ids = Object.keys(sel).filter((k) => sel[k]);
  const make = async () => {
    try {
      const r = await axios.post(`${base}/deposits`, { deposit_to_id: depTo, date, payment_gl_ids: ids });
      setLastDep(r.data.txn_id); setMsg(`Deposited ${money(r.data.deposit_total)}`); setSel({}); await load();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Deposit failed'); }
  };
  const removeLine = async (glId) => {
    if (!lastDep) return;
    try { await axios.post(`${base}/deposits/${lastDep}/remove-line`, { payment_gl_id: glId }); setMsg('Line removed'); await load(); }
    catch (e) { setMsg(e?.response?.data?.detail || 'Remove failed'); }
  };
  const slip = async () => {
    if (!lastDep) return;
    const r = await axios.get(`${base}/deposits/${lastDep}/slip`, { responseType: 'blob' });
    downloadBlob(r.data, 'deposit-slip.pdf', 'application/pdf');
  };
  const banks = accounts.filter((a) => ['Bank', 'Other Current Asset'].includes(a.type));
  return (
    <Card className={panelCls(isDark)} data-testid="banking-deposits"><CardContent className="p-4 space-y-3">
      <div className="flex items-end gap-2 flex-wrap">
        <Field label="Deposit to" sub={sub}><Select value={depTo} onValueChange={setDepTo}><SelectTrigger className="w-48" data-testid="dep-to"><SelectValue /></SelectTrigger><SelectContent>{banks.map((a) => <SelectItem key={a.account_id} value={a.account_id}>{a.full_name}</SelectItem>)}</SelectContent></Select></Field>
        <Field label="Date" sub={sub}><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="dep-date" /></Field>
        <Field label="Method filter" sub={sub}><Select value={payMeth} onValueChange={setPayMeth}><SelectTrigger className="w-36" data-testid="dep-method"><SelectValue /></SelectTrigger><SelectContent>{['all', 'Check', 'Cash', 'Visa', 'MasterCard'].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></Field>
        <Field label="Sort" sub={sub}><Select value={sort} onValueChange={setSort}><SelectTrigger className="w-32" data-testid="dep-sort"><SelectValue /></SelectTrigger><SelectContent>{['date', 'method', 'customer'].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></Field>
        <Button className="bg-emerald-600 text-white" onClick={make} disabled={!depTo || !ids.length} data-testid="dep-make">Make deposit</Button>
        {lastDep && <><Button variant="outline" onClick={slip} data-testid="dep-slip"><FileDown className="h-4 w-4 mr-1" />Slip PDF</Button></>}
        {msg && <span className={`text-sm ${sub}`} data-testid="dep-msg">{msg}</span>}
      </div>
      {rows.length === 0 ? <p className={`text-sm ${sub}`} data-testid="dep-empty">No undeposited payments.</p> : (
        <table className="w-full text-sm"><tbody data-testid="dep-rows">
          {rows.map((r) => (
            <tr key={r.gl_id} className={`border-b ${isDark ? 'border-slate-800' : 'border-slate-100'}`}>
              <td className="p-2"><input type="checkbox" checked={!!sel[r.gl_id]} onChange={(e) => setSel({ ...sel, [r.gl_id]: e.target.checked })} data-testid={`dep-sel-${r.gl_id}`} /></td>
              <td className="p-2">{r.date}</td><td className="p-2">{r.name}</td><td className="p-2">{r.pay_meth}</td>
              <td className="p-2 text-right tabular-nums">{money(r.amount)}</td>
            </tr>
          ))}
        </tbody></table>
      )}
      {lastDep && <p className={`text-xs ${sub}`}>Deposit saved. Select a line above already on it and use the button to remove: <Button size="sm" variant="ghost" onClick={() => ids[0] && removeLine(ids[0])} data-testid="dep-remove-line">Remove selected from deposit</Button></p>}
    </CardContent></Card>
  );
}

function Transfer({ base, accounts, isDark, onDone }) {
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';
  const [from, setFrom] = useState(''); const [to, setTo] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState(''); const [msg, setMsg] = useState('');
  const go = async () => {
    try { await axios.post(`${base}/transfer`, { from_id: from, to_id: to, date, amount: toCents(amount) }); setMsg('Transfer posted'); onDone(); }
    catch (e) { setMsg(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <Card className={panelCls(isDark)} data-testid="banking-transfer-form"><CardContent className="p-4 grid gap-3 sm:grid-cols-2">
      <Field label="From" sub={sub}><Select value={from} onValueChange={setFrom}><SelectTrigger data-testid="xfer-from"><SelectValue placeholder="From" /></SelectTrigger><SelectContent>{accounts.map((a) => <SelectItem key={a.account_id} value={a.account_id}>{a.full_name}</SelectItem>)}</SelectContent></Select></Field>
      <Field label="To" sub={sub}><Select value={to} onValueChange={setTo}><SelectTrigger data-testid="xfer-to"><SelectValue placeholder="To" /></SelectTrigger><SelectContent>{accounts.map((a) => <SelectItem key={a.account_id} value={a.account_id}>{a.full_name}</SelectItem>)}</SelectContent></Select></Field>
      <Field label="Date" sub={sub}><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="xfer-date" /></Field>
      <Field label="Amount" sub={sub}><Input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" data-testid="xfer-amount" /></Field>
      <div className="sm:col-span-2 flex items-center gap-3">
        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={go} disabled={!from || !to || !amount} data-testid="xfer-save"><ArrowRightLeft className="h-4 w-4 mr-1" />Transfer</Button>
        {msg && <span className={`text-sm ${sub}`}>{msg}</span>}
      </div>
    </CardContent></Card>
  );
}

function Reconcile({ base, acctId, accounts, isDark }) {
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';
  const [beginData, setBeginData] = useState(null);
  const [stmtDate, setStmtDate] = useState(new Date().toISOString().slice(0, 10));
  const [ending, setEnding] = useState('');
  const [svc, setSvc] = useState(''); const [interest, setInterest] = useState('');
  const [marked, setMarked] = useState({});
  const [msg, setMsg] = useState('');
  const [history, setHistory] = useState([]);
  const [disc, setDisc] = useState(null);
  const [prevRep, setPrevRep] = useState(null);
  const acct = accounts.find((a) => a.account_id === acctId);
  const isCard = acct?.type === 'Credit Card';

  const loadAux = useCallback(async () => {
    if (!acctId) return;
    try {
      setHistory((await axios.get(`${base}/reconcile/history/${acctId}`)).data || []);
      setDisc((await axios.get(`${base}/reconcile/discrepancy/${acctId}`)).data);
    } catch { /* noop */ }
  }, [base, acctId]);
  useEffect(() => { loadAux(); }, [loadAux]);

  const begin = async () => {
    const r = await axios.post(`${base}/reconcile/begin`, { account_id: acctId, statement_date: stmtDate });
    setBeginData(r.data);
    const m = {}; [...r.data.deposits, ...r.data.payments].forEach((x) => { if (x.marked) m[x.gl_id] = true; });
    setMarked(m);
    if (r.data.draft) { setStmtDate(r.data.draft.statement_date || stmtDate); setEnding(r.data.draft.ending_balance != null ? (r.data.draft.ending_balance / 100).toFixed(2) : ''); }
    setMsg(`Beginning ${money(r.data.beginning_balance)} · ${r.data.reconciled_count} reconciled${r.data.draft ? ' · draft resumed' : ''}`);
  };
  const all = beginData ? [...beginData.deposits, ...beginData.payments] : [];
  const markedIds = Object.keys(marked).filter((k) => marked[k]);
  const body = () => ({ account_id: acctId, statement_date: stmtDate, ending_balance: toCents(ending), marked_gl_ids: markedIds,
    service_charge: svc ? { amount: toCents(svc), date: stmtDate } : null,
    interest: interest ? { amount: toCents(interest), date: stmtDate } : null });
  const now = async (adjustment) => {
    try {
      const r = await axios.post(`${base}/reconcile/now`, { ...body(), adjustment: !!adjustment });
      if (r.data.completed) { setMsg('Reconciled!'); setBeginData(null); loadAux(); }
      else {
        setMsg(`Difference ${money(r.data.difference)} — cleared ${money(r.data.cleared_balance)}.`);
        // eslint-disable-next-line no-alert
        if (window.confirm(`Off by ${money(r.data.difference)}. Post a Reconciliation Adjustment to force completion?`)) now(true);
      }
    } catch (e) { setMsg(e?.response?.data?.detail || 'Failed'); }
  };
  const leave = async () => { await axios.post(`${base}/reconcile/leave`, body()); setMsg('Draft saved'); setBeginData(null); };
  const undo = async () => { await axios.post(`${base}/reconcile/undo`, { account_id: acctId }); setMsg('Undone'); setBeginData(null); loadAux(); };
  const prev = async (recon, detail) => {
    const r = await axios.get(`${base}/reconcile/previous/${recon.recon_id}?detail=${detail}`);
    setPrevRep(r.data);
  };
  const report = async (recon, fmt, detail) => {
    const r = await axios.get(`${base}/reconcile/report/${recon.recon_id}?fmt=${fmt}&detail=${detail}`, { responseType: 'blob' });
    downloadBlob(r.data, `reconciliation.${fmt}`, fmt === 'csv' ? 'text/csv' : 'application/pdf');
  };
  const hasDisc = disc && disc.changes && disc.changes.length > 0;
  return (
    <Card className={panelCls(isDark)} data-testid="banking-reconcile"><CardContent className="p-4 space-y-3">
      {hasDisc && (
        <div className="flex items-center gap-2 rounded bg-amber-500/15 border border-amber-500/40 p-2 text-sm text-amber-500" data-testid="recon-disc-badge">
          <AlertTriangle className="h-4 w-4" /> {disc.changes.length} discrepancy change(s) since reconciliation
        </div>
      )}
      <div className="flex items-end gap-2 flex-wrap">
        <Field label="Statement date" sub={sub}><Input type="date" value={stmtDate} onChange={(e) => setStmtDate(e.target.value)} data-testid="recon-stmt-date" /></Field>
        <Field label="Ending balance" sub={sub}><Input value={ending} onChange={(e) => setEnding(e.target.value)} placeholder="0.00" data-testid="recon-ending" /></Field>
        <Field label={isCard ? 'Finance charge' : 'Service charge'} sub={sub}><Input value={svc} onChange={(e) => setSvc(e.target.value)} placeholder="0.00" data-testid="recon-svc" /></Field>
        <Field label="Interest earned" sub={sub}><Input value={interest} onChange={(e) => setInterest(e.target.value)} placeholder="0.00" data-testid="recon-interest" /></Field>
        <Button variant="outline" onClick={begin} data-testid="recon-begin"><CheckSquare className="h-4 w-4 mr-1" />Begin / resume</Button>
        {beginData && <>
          <Button className="bg-emerald-600 text-white" onClick={() => now(false)} data-testid="recon-now">Reconcile Now</Button>
          <Button variant="outline" onClick={leave} data-testid="recon-leave">Leave (draft)</Button>
        </>}
        <Button variant="ghost" onClick={undo} data-testid="recon-undo">Undo Last</Button>
      </div>
      {msg && <div className={`text-sm ${sub}`} data-testid="recon-msg">{msg}</div>}
      {prevRep && (
        <div data-testid="recon-prev-view">
          <div className="flex justify-end"><Button size="sm" variant="ghost" onClick={() => setPrevRep(null)} data-testid="recon-prev-close">Close</Button></div>
          <ReportDocument rep={prevRep} title="Previous Reconciliation" periodLabel={prevRep.statement_date ? `Statement ${prevRep.statement_date}` : ''} />
        </div>
      )}
      {beginData && (
        <div className="grid gap-1 max-h-72 overflow-auto text-sm" data-testid="recon-rows">
          {all.map((r) => (
            <label key={r.gl_id} className="flex items-center gap-2">
              <input type="checkbox" checked={!!marked[r.gl_id]} onChange={(e) => setMarked({ ...marked, [r.gl_id]: e.target.checked })} data-testid={`recon-mark-${r.gl_id}`} />
              <span className="w-24">{r.date}</span><span className="tabular-nums">{money(r.amount)}</span>
            </label>
          ))}
        </div>
      )}
      {history.filter((h) => h.completed).length > 0 && (
        <div className="space-y-1" data-testid="recon-history">
          <span className={`text-xs ${sub}`}>Previous reconciliations</span>
          {history.filter((h) => h.completed).map((h) => (
            <div key={h.recon_id} className="flex items-center gap-2 text-sm">
              <span className="w-28">{h.statement_date}</span>
              <span className="tabular-nums w-28">{money(h.ending_balance)}</span>
              <Button size="sm" variant="ghost" onClick={() => prev(h, false)} data-testid={`recon-prev-sum-${h.recon_id}`}>Summary</Button>
              <Button size="sm" variant="ghost" onClick={() => prev(h, true)} data-testid={`recon-prev-det-${h.recon_id}`}>Detail</Button>
              <Button size="sm" variant="ghost" onClick={() => report(h, 'pdf', true)} data-testid={`recon-pdf-${h.recon_id}`}><FileDown className="h-3.5 w-3.5 mr-1" />PDF</Button>
              <Button size="sm" variant="ghost" onClick={() => report(h, 'csv', true)} data-testid={`recon-csv-${h.recon_id}`}>CSV</Button>
            </div>
          ))}
        </div>
      )}
    </CardContent></Card>
  );
}
